/**
 * @fileoverview Bug Report Detail Fields Configuration
 *
 * Configuration for editable fields on the bug report detail page.
 */

import { DetailFieldsConfig } from 'shared/components/EditableDetailField/types';
import {
  ListBullets,
  Tag,
  TextT,
  Star,
  CheckSquare,
} from '@phosphor-icons/react';
import { API_URL } from 'constants/index';
import { BugPriority, BugStatus } from 'shared/types/bug-report';
import { PREDEFINED_BUG_REPORT_TAGS } from '../bugReportTags';

export const BUG_REPORT_DETAIL_FIELDS_CONFIG: DetailFieldsConfig = {
  entityName: 'bug-report',
  entityPrefix: 'BUG',
  apiUpdateEndpoint: `${API_URL}/bug-reports/{id}`,

  fields: [
    // Title (text)
    {
      id: 'title',
      label: 'Titel',
      type: 'text',
      icon: TextT,
      displayValue: (bug) => bug?.title || '-',
      extractValue: (bug) => bug?.title || '',
      buildUpdatePayload: (value) => ({ title: value }),
      placeholder: 'Voeg een titel toe voor de bug...',
    },

    // Description (textarea)
    {
      id: 'description',
      label: 'Beschrijving',
      type: 'textarea',
      icon: ListBullets,
      displayValue: (bug) => bug?.description || '-',
      extractValue: (bug) => bug?.description || '',
      buildUpdatePayload: (value) => ({ description: value }),
      placeholder: 'Voeg een gedetailleerde beschrijving toe van de bug...',
    },

    // Priority (picker)
    {
      id: 'priority',
      label: 'Prioriteit',
      type: 'picker',
      pickerMode: 'bug-priority',
      icon: Star,
      displayValue: (bug) => bug?.priority || '-',
      extractValue: (bug) => bug?.priority || BugPriority.MEDIUM,
      buildUpdatePayload: (value) => ({ priority: value }),
    },

    // Status (picker)
    {
      id: 'status',
      label: 'Status',
      type: 'picker',
      pickerMode: 'bug-status',
      icon: CheckSquare,
      displayValue: (bug) => bug?.status || '-',
      extractValue: (bug) => bug?.status || BugStatus.REPORTED,
      buildUpdatePayload: (value) => ({ status: value }),
    },

    // Note: assignedTo is handled in the Reporter & Assignee section above with proper user display

    // Tags (multi-select picker with predefined options)
    {
      id: 'tags',
      label: 'Tags',
      type: 'picker',
      pickerMode: 'custom-tags',
      icon: Tag,
      displayValue: (bug) => bug?.tags && bug.tags.length > 0 ? bug.tags.join(', ') : '-',
      extractValue: (bug) => bug?.tags || [],
      buildUpdatePayload: (value) => ({
        tags: Array.isArray(value) ? value : []
      }),
      pickerConfig: {
        multiSelect: true,
        customOptions: PREDEFINED_BUG_REPORT_TAGS,
      }
    },

    // Note: Epic and Type are not editable here because the backend model
    // for bug reports currently does not support these fields.
  ]
};
